import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { firestoreService } from '@/data/services/firestoreService';
import { auth } from '@/data/services/firebase';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { SavedContentEntity } from '@/domain/entities/savedContent/savedContentEntity';
import { UserMediaRemapper } from '@/domain/remappers/userMedia/userMediaRemapper';
import { SavedContentRemapper } from '@/domain/remappers/savedContent/savedContentRemapper';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * Media Service
 * 
 * Data layer service for media operations.
 * 
 * ⚠️ NOT A SINGLETON - Create new instance for each use
 * Benefits:
 * - No concurrency issues (each request isolated)
 * - No shared state between calls
 * - Easy to test (fresh instance per test)
 * - Proper garbage collection
 * 
 * Architecture:
 * UseCase → MediaService → CloudFunctionsService → Firebase
 *                       ↓
 *                   Remapper (DTO → Entity)
 */
export class MediaService {
  private cloudFunctionsService: typeof cloudFunctionsService;

  constructor(cloudFunctionsServiceInstance = cloudFunctionsService) {
    this.cloudFunctionsService = cloudFunctionsServiceInstance;
  }

  /**
   * Fetch feed from Firebase Cloud Functions
   * Returns UserMediaEntity[] (domain entities, not DTOs)
   */
  async fetchFeed(): Promise<{ medias: UserMediaEntity[]; hasMore: boolean }> {
    try {
      // 1. Fetch raw data from Cloud Functions
      const response = await this.cloudFunctionsService.fetchFeed();

      // Mock returning a big list of medias. Assign different IDs to each media.
      // const bigMediaList = [];
      // for (let i = 0; i < 50; i++) {
      //   for (const media of response.medias) {
      //     bigMediaList.push({ ...media, id: `${media.id}_copy_${i}` });
      //   }
      // }
      // response.medias = bigMediaList;

      // 2. Map DTOs to Entities using remapper
      const entities = response.medias.map(dto => UserMediaRemapper.toEntity(dto));

      return {
        medias: entities,
        hasMore: response.hasMore
      };
    } catch (error) {
      appLogger.error('fetchFeed error', 'MediaService.fetchFeed', { error: error as Error });
      // Return empty response on error (matching Flutter behavior)
      return {
        medias: [],
        hasMore: false
      };
    }
  }
  /**
   * Save or remove a content item from current user's watch later list
   * Flutter source: ContentDataSource.saveContent
   */
  async saveContent(entity: SavedContentEntity, shouldSave: boolean): Promise<boolean> {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      appLogger.error('No authenticated user for saving content', 'MediaService.saveContent');
      return false;
    }

    const dto = SavedContentRemapper.toDto(entity);
    return firestoreService.saveContent(currentUser.uid, dto, shouldSave);
  }
  /**
   * Hide a content item (matching Flutter MediasRepository.hideContent)
   */
  async hideContent(contentId: string): Promise<boolean> {
    return this.cloudFunctionsService.hideContent(contentId);
  }

  /**
   * Increment views count for a content item
   * (matching Flutter MediasRepository.incrementContentViews →
   * CloudFunctionsService.incrementContentViews).
   */
  async incrementContentViews(userId: string, contentId: string): Promise<boolean> {
    return this.cloudFunctionsService.incrementContentViews(userId, contentId);
  }

  /**
   * Save the current playback position to the user's watch history
   * (matching Flutter MediasRepository.saveToWatchHistory →
   * CloudFunctionsService.saveToWatchHistory).
   */
  async saveToWatchHistory(
    contentId: string,
    currentMillis: number,
    totalMillis: number,
  ): Promise<boolean> {
    return this.cloudFunctionsService.saveToWatchHistory(contentId, currentMillis, totalMillis);
  }

  /**
   * Delete a content item (matching Flutter MediasRepository.deleteContent →
   * CloudFunctionsService.deleteContentStorage).
   */
  async deleteContent(contentId: string): Promise<boolean> {
    return this.cloudFunctionsService.deleteContentStorage(contentId);
  }

  /**
   * Force-publish a draft or scheduled content item now
   * (matching Flutter MediasRepository.publishContent →
   * CloudFunctionsService.forcePublishContent).
   */
  async publishContent(contentId: string): Promise<boolean> {
    return this.cloudFunctionsService.forcePublishContent(contentId);
  }

  /**
   * Retry the premium-video payment for a content whose upload payment failed
   * (matching Flutter MediasRepository.retryPremiumVideoPayment →
   * CloudFunctionsService.retryPremiumVideoPayment, callable
   * `retryPremiumVideoPayment` — lib/data/repository/medias_repository.dart:389-391).
   */
  async retryPremiumVideoPayment(contentId: string): Promise<boolean> {
    return this.cloudFunctionsService.retryPremiumVideoPayment(contentId);
  }

  /**
   * Retry the boost-video payment for a content whose boost payment failed
   * (matching Flutter MediasRepository.retryBoostVideoPayment →
   * CloudFunctionsService.retryBoostVideoPayment, callable
   * `retryBoostVideoPayment` — lib/data/repository/medias_repository.dart:393-395).
   */
  async retryBoostVideoPayment(contentId: string): Promise<boolean> {
    return this.cloudFunctionsService.retryBoostVideoPayment(contentId);
  }

  /**
   * Link two of the current user's media items together
   * (matching Flutter MediasRepository.linkContent).
   */
  async linkContent(source: UserMediaEntity, destination: UserMediaEntity): Promise<boolean> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      appLogger.error('No authenticated user for linkContent', 'MediaService.linkContent');
      return false;
    }
    return firestoreService.linkContent(
      currentUser.uid,
      source.id,
      source.relativePath,
      destination.id,
      destination.relativePath,
    );
  }

  /**
   * Unlink a media item from its peer
   * (matching Flutter MediasRepository.unlinkContent).
   * Returns the peer media id on success, or null on failure.
   */
  async unlinkContent(source: UserMediaEntity): Promise<string | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      appLogger.error('No authenticated user for unlinkContent', 'MediaService.unlinkContent');
      return null;
    }
    if (!source.contentLink) {
      appLogger.error('unlinkContent called on media without contentLink', 'MediaService.unlinkContent');
      return null;
    }
    return firestoreService.unlinkContent(currentUser.uid, source.id, source.contentLink);
  }
}

// Note: For utility methods (getThumbnailUrl, isVideo, isImage, getMediaUrl),
// use MediaUtils class - it provides static utility methods with no state